import BaseObjectTypeEnum from "@/enums/BaseObjectTypeEnum";
import SelectModeEnum from "@/enums/SelectModeEnum";
import Criteria from "@/types/FilterTree/Criteria";
import SubFilter from "@/types/FilterTree/SubFilter";
import TreeNode from "@/types/TreeNode";
import FilterTreeStore from "../FilterTreeStore";
import Pin from "@/types/FilterTree/Pin";
import IConfigTree from "@/interfaces/IConfigTree";
import FilterT from "@/types/FilterTree/FilterT";

/** Strategy de selection dans le FilterTree */
export interface ISelectModeStrategy {

    Init(nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean, configTree: IConfigTree, cleaned: boolean, grouping : FilterT);
    SetPins(nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean, cleaned: boolean);
    StopPropagation(isContextMenu: boolean, node: TreeNode): boolean;

    SelectNodes(selectedNodes: TreeNode[], included: boolean, nodes : TreeNode[], pins : Pin[], maxPinId: number, isHistoricMode: boolean, isCheckOnly : boolean); 
    UpdateNode(isHistoricMode: boolean, isCheckOnly: boolean, node: TreeNode);

    SetHistoricMode(nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean): void;
    SetCheckOnly(nodes : TreeNode[], isHistoricMode: boolean, isCheckOnly : boolean): void;
    ToggleIncluded(pin: Pin, nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean): void;
    RemoveAllPins(nodes: TreeNode[], isHistoricMode: boolean, isCheckOnly: boolean): void;
    SelectAllNodes(nodes: TreeNode[], isHistoricMode: boolean, isCheckOnly: boolean): void;
    RemovePins(removePins: Pin[], nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean);

    GetSelectMode() : SelectModeEnum;
}

/** Racine d'un comportement de selection */
export abstract class SelectModeStrategy implements ISelectModeStrategy {
    /** Context FilterTree (store Vuex consomateur de service) */
    protected store: FilterTreeStore;
    
    protected selectMode: SelectModeEnum = SelectModeEnum.Single;

    /** Constructeur par défaut */
    public constructor(context: FilterTreeStore) {
        this.store = context;
    }

    public abstract Init(nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean, configTree: IConfigTree, cleaned: boolean, grouping : FilterT);
    public abstract SetPins(nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean, cleaned: boolean);    
    public abstract StopPropagation(isContextMenu: boolean, node: TreeNode): boolean;
    public abstract SelectNodes(selectedNodes: TreeNode[], included: boolean, nodes : TreeNode[], pins : Pin[], maxPinId: number, isHistoricMode: boolean, isCheckOnly : boolean);
    public abstract UpdateNode(isHistoricMode: boolean, isCheckOnly: boolean, node: TreeNode);
    public abstract SetHistoricMode(nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean): void;
    public abstract SelectAllNodes(nodes: TreeNode[], isHistoricMode: boolean, isCheckOnly: boolean): void;

    public SetCheckOnly(nodes: TreeNode[], isHistoricMode: boolean, isCheckOnly: boolean): void {

        for (let node of nodes) {
            this.UpdateNode(isHistoricMode, isCheckOnly, node);
        }

        this.store.context.commit('setBases', { nodes: nodes });
        this.UpdateInvolvedNodes(nodes, isHistoricMode);
    }

    public ToggleIncluded(pin: Pin, nodes: TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly: boolean): void {

        let indexPin = pins.findIndex(x => x.Id === pin.Id);
        pins[indexPin].Included = !(pins[indexPin].Included);

        for (let node of nodes) {
            let indexSelect = node.Selections.findIndex(x => x.Id == pin.Id);
            if (indexSelect != -1) {
                node.Selections[indexSelect].Included = !node.Selections[indexSelect].Included;
                this.UpdateNode(isHistoricMode, isCheckOnly, node);
            }
        }

        this.store.context.commit('setBases', { nodes: nodes, pins: pins });
        this.UpdateInvolvedNodes(nodes, isHistoricMode);
    }

    protected Cleaned(nodes: TreeNode[], isHistoricMode: boolean, isCheckOnly: boolean): TreeNode[] {
     
        for (let node of nodes) {
            this.cleanNode(node);
        }

        return nodes;
    }

    public RemoveAllPins(nodes: TreeNode[], isHistoricMode: boolean, isCheckOnly: boolean): void {
        
        for (let node of nodes) {
            this.cleanNode(node);
        }

        this.store.context.commit('setBases', { nodes: nodes, pins: [], maxPinId: 0 });
        this.store.context.commit('updateInvolvedNodes', [] as TreeNode[]);
    }

    public RemovePins(removePins: Pin[], nodes : TreeNode[], pins : Pin[], isHistoricMode: boolean, isCheckOnly : boolean) {
        console.debug("FilterTree - RemovePins - remoPins -  : ",  removePins);

        for(let removePin of removePins) {
            for (let node of nodes) {
                let indexSelect = node.Selections.findIndex(x => x.Id === removePin.Id);

                if (indexSelect !== -1) {
                    node.Selections.splice(indexSelect, 1);
                    this.UpdateNode(isHistoricMode, isCheckOnly, node);
                }
            }

            let indexPin = pins.findIndex(x => x.Id === removePin.Id);
            pins.splice(indexPin, 1);
        }

        let maxPinId = 0;
        for(let pin of pins) {
            if (pin.Id > maxPinId)
                maxPinId = pin.Id;
        }

        this.store.context.commit('setBases', { nodes: nodes, pins: pins, maxPinId: maxPinId});
        this.UpdateInvolvedNodes(nodes, isHistoricMode);
    }

    public GetSelectMode() : SelectModeEnum {
        return this.selectMode;
    }

    protected UpdateInvolvedNodes(nodes : TreeNode[], isHistoricMode: boolean){
        let involvedNodes: TreeNode[] = [] as TreeNode[];
                        
        for (let node of nodes) {
            if (node.Selections && node.Selections.length > 0)
                involvedNodes.push(node);
        }

        // Si mode historique désactivé, on filtre les noeuds dépréciés
        if (!isHistoricMode)
            involvedNodes = involvedNodes.filter(n => !n.Depreciated);

        this.store.context.commit('updateInvolvedNodes', involvedNodes);
    }

     /** Réinitialise les états de l'ensemble des noeuds */
    protected cleanNode(node : TreeNode): TreeNode {
        node.Selected = false;
        node.Checked = false;
        node.CheckLeader = false;
        node.Indeterminate = false;
        node.NestedChecked = false;
        node.Disabled = false;
        node.Forced = false;
        node.Selections = [];
        return node;
    }
}